import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { getStakeholders, createStakeholder, updateStakeholder, deleteStakeholder, getEmployees, getStakeholderComparison, getProjects, getProjectPaymentDistribution } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Plus,
  PencilSimple,
  Trash,
  Users,
  Crown,
  Clock,
  ListChecks,
  ChartBar,
  Trophy,
  Wallet,
  ArrowRight,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export function StakeholdersPage() {
  const { t } = useLanguage();
  const [stakeholders, setStakeholders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [distribution, setDistribution] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStakeholder, setEditingStakeholder] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', employee_ids: [], is_active: true,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [selectedMonth]);

  useEffect(() => {
    if (selectedProjectId) { fetchDistribution(selectedProjectId); }
    else { setDistribution(null); }
  }, [selectedProjectId]);

  const fetchData = async () => {
    try {
      const [year, month] = selectedMonth.split('-');
      const [stakeholdersRes, employeesRes, comparisonRes, projectsRes] = await Promise.all([
        getStakeholders(), getEmployees(), getStakeholderComparison({ month, year }), getProjects(),
      ]);
      setStakeholders(stakeholdersRes.data);
      setEmployees(employeesRes.data);
      setComparison(comparisonRes.data);
      setProjects(projectsRes.data || []);
    } catch (error) { toast.error(t('errorOccurred')); }
    finally { setLoading(false); }
  };

  const fetchDistribution = async (projectId) => {
    try { const res = await getProjectPaymentDistribution(projectId); setDistribution(res.data); }
    catch { setDistribution(null); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStakeholder) { await updateStakeholder(editingStakeholder._id, formData); }
      else { await createStakeholder(formData); }
      toast.success(t('successSaved'));
      setIsDialogOpen(false); resetForm(); fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  const handleEdit = (sh) => {
    setEditingStakeholder(sh);
    setFormData({ name: sh.name || '', email: sh.email || '', phone: sh.phone || '', employee_ids: sh.employee_ids || [], is_active: sh.is_active !== false });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('deleteStakeholder'))) {
      try { await deleteStakeholder(id); toast.success(t('successDeleted')); fetchData(); }
      catch { toast.error(t('errorOccurred')); }
    }
  };

  const resetForm = () => {
    setEditingStakeholder(null);
    setFormData({ name: '', email: '', phone: '', employee_ids: [], is_active: true });
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      employee_ids: prev.employee_ids.includes(empId) ? prev.employee_ids.filter(id => id !== empId) : [...prev.employee_ids, empId]
    }));
  };

  const getMaxHours = () => comparison?.stakeholders ? Math.max(...comparison.stakeholders.map(s => s.total_hours), 1) : 1;
  const getMaxTasks = () => comparison?.stakeholders ? Math.max(...comparison.stakeholders.map(s => s.tasks_completed), 1) : 1;

  return (
    <Layout title={t('stakeholders')}>
      <div className="space-y-6" data-testid="stakeholders-page">
        {/* Comparison Report */}
        <div className="valhalla-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6] flex items-center gap-2">
              <ChartBar weight="duotone" className="w-5 h-5 text-[#E2B05E]" />{t('monthlyComparison')}
            </h3>
            <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="valhalla-input w-44" />
          </div>

          {comparison?.stakeholders && comparison.stakeholders.length > 0 ? (
            <div className="space-y-6">
              {comparison.stakeholders.map((sh, index) => (
                <div key={sh.stakeholder_id} className="p-4 rounded-xl bg-[#0B0E14] border border-[#242E42]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {index === 0 && comparison.stakeholders.length > 1 && <Trophy weight="fill" className="w-6 h-6 text-[#E2B05E]" />}
                      <div>
                        <h4 className="font-semibold text-[#F3F4F6]">{sh.stakeholder_name}</h4>
                        <p className="text-sm text-[#9CA3AF]">{sh.employee_count} {t('assignedEmployees')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-[#9CA3AF]">{t('productivityLabel')}</p>
                      <p className="font-['Outfit'] text-xl font-bold text-[#3AB8D6]">{sh.productivity_score}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-[#9CA3AF] flex items-center gap-1"><Clock className="w-4 h-4" /> {t('hoursLabel')}</span>
                        <span className="text-[#F3F4F6] font-bold">{sh.total_hours.toFixed(1)}h</span>
                      </div>
                      <Progress value={(sh.total_hours / getMaxHours()) * 100} className="h-3 bg-[#242E42]" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-[#9CA3AF] flex items-center gap-1"><ListChecks className="w-4 h-4" /> {t('tasksLabel')}</span>
                        <span className="text-[#F3F4F6] font-bold">{sh.tasks_completed}</span>
                      </div>
                      <Progress value={(sh.tasks_completed / getMaxTasks()) * 100} className="h-3 bg-[#242E42]" />
                    </div>
                    <div>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-[#9CA3AF]">{t('taskValue')}</span>
                        <span className="text-[#E2B05E] font-bold">${sh.tasks_value?.toLocaleString() || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="p-4 rounded-xl bg-gradient-to-r from-[#E2B05E]/20 to-[#3AB8D6]/20 border border-[#E2B05E]/30">
                <h4 className="font-semibold text-[#F3F4F6] mb-3">{t('monthTotals')}</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div><p className="text-2xl font-bold text-[#E2B05E]">{comparison.totals.total_hours?.toFixed(1)}h</p><p className="text-sm text-[#9CA3AF]">{t('totalHours')}</p></div>
                  <div><p className="text-2xl font-bold text-[#3AB8D6]">{comparison.totals.total_tasks}</p><p className="text-sm text-[#9CA3AF]">{t('completedTasksLabel')}</p></div>
                  <div><p className="text-2xl font-bold text-emerald-400">${comparison.totals.total_value?.toLocaleString() || 0}</p><p className="text-sm text-[#9CA3AF]">{t('totalValue')}</p></div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-[#9CA3AF] text-center py-8">{t('noDataPeriod')}</p>
          )}
        </div>

        {/* Revenue Distribution */}
        <div className="valhalla-card p-6" data-testid="revenue-distribution">
          <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6] flex items-center gap-2 mb-4">
            <Wallet weight="duotone" className="w-5 h-5 text-[#E2B05E]" />{t('revenueDistribution')}
          </h3>
          <div className="flex items-center gap-4 mb-6">
            <Select value={selectedProjectId || "none"} onValueChange={(v) => setSelectedProjectId(v === "none" ? "" : v)}>
              <SelectTrigger className="w-80 valhalla-input" data-testid="project-select-distribution"><SelectValue placeholder={t('selectProjectDist')} /></SelectTrigger>
              <SelectContent className="bg-[#151B26] border-[#242E42]">
                <SelectItem value="none">-- {t('selectProjectDist')} --</SelectItem>
                {projects.map(p => (<SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          {distribution ? (
            distribution.total_received > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 rounded-xl bg-[#0B0E14] border border-[#242E42] text-center">
                    <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{t('totalReceived')}</p>
                    <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">${distribution.total_received?.toLocaleString()}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[#0B0E14] border border-[#E2B05E]/30 text-center">
                    <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{t('companyLabel')} ({distribution.company_percentage}%)</p>
                    <p className="font-['Outfit'] text-2xl font-bold text-[#E2B05E]">${distribution.company_share?.toLocaleString()}</p>
                  </div>
                  <div className="p-4 rounded-xl bg-[#0B0E14] border border-[#3AB8D6]/30 text-center">
                    <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{t('stakeholderPool')} ({distribution.stakeholder_percentage}%)</p>
                    <p className="font-['Outfit'] text-2xl font-bold text-[#3AB8D6]">${distribution.stakeholder_pool?.toLocaleString()}</p>
                  </div>
                </div>
                {distribution.distribution?.length > 0 ? (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-[#9CA3AF] uppercase tracking-wider">{t('distributionByStakeholder')}</h4>
                    {distribution.distribution.map((d) => (
                      <div key={d.stakeholder_id} className="p-4 rounded-xl bg-[#0B0E14] border border-[#242E42]">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2"><Crown weight="fill" className="w-5 h-5 text-[#E2B05E]" /><span className="font-semibold text-[#F3F4F6]">{d.stakeholder_name}</span></div>
                          <div className="text-right">
                            <span className="font-['Outfit'] text-xl font-bold text-emerald-400">${d.share_amount?.toLocaleString()}</span>
                            <span className="text-sm text-[#9CA3AF] ml-2">({d.share_percentage}%)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#9CA3AF] mb-2"><Clock className="w-4 h-4" /><span>{d.total_hours?.toFixed(1)} {t('hoursWorked')}</span></div>
                        {d.employees?.length > 0 && (
                          <div className="mt-2 space-y-1 pl-4 border-l-2 border-[#242E42]">
                            {d.employees.map((emp, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-[#9CA3AF]">{emp.name}</span>
                                <span className="text-[#F3F4F6]">{emp.hours}h <ArrowRight className="w-3 h-3 inline" /> ${emp.share?.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (<p className="text-[#9CA3AF] text-center py-4">{t('noHoursForDistribution')}</p>)}
              </div>
            ) : (<p className="text-[#9CA3AF] text-center py-4">{t('noPaymentsProject')}</p>)
          ) : (<p className="text-[#9CA3AF] text-center py-4">{t('selectProjectToSee')}</p>)}
        </div>

        {/* Stakeholder Management Header */}
        <div className="flex justify-between items-center">
          <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">{t('stakeholderManagement')}</h3>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold" data-testid="new-stakeholder-btn">
                <Plus className="w-4 h-4 mr-2" />{t('newStakeholder')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#151B26] border-[#242E42] max-w-md">
              <DialogHeader><DialogTitle className="text-[#F3F4F6] font-['Outfit']">{editingStakeholder ? t('editStakeholder') : t('newStakeholder')}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('name')}</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="valhalla-input" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('email')}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="valhalla-input" required />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('phone2')}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="valhalla-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#9CA3AF]">{t('assignedEmployeesLabel')}</Label>
                  <div className="max-h-48 overflow-y-auto space-y-2 p-3 rounded-lg bg-[#0B0E14] border border-[#242E42]">
                    {employees.map(emp => (
                      <div key={emp._id} className="flex items-center gap-2">
                        <Checkbox id={emp._id} checked={formData.employee_ids.includes(emp._id)} onCheckedChange={() => toggleEmployee(emp._id)} />
                        <Label htmlFor={emp._id} className="text-[#F3F4F6] cursor-pointer">{emp.name} {emp.last_name}</Label>
                      </div>
                    ))}
                    {employees.length === 0 && <p className="text-[#9CA3AF] text-sm">{t('noEmployeesAvailable')}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="is_active" checked={formData.is_active} onCheckedChange={(checked) => setFormData({...formData, is_active: checked})} />
                  <Label htmlFor="is_active" className="text-[#9CA3AF] cursor-pointer">{t('activeLabel')}</Label>
                </div>
                <div className="flex justify-end gap-3 pt-4 border-t border-[#242E42]">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-[#242E42] text-[#9CA3AF]">{t('cancel')}</Button>
                  <Button type="submit" className="bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14]">{t('save')}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stakeholders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stakeholders.map((sh) => (
            <div key={sh._id} className="valhalla-card p-5" data-testid={`stakeholder-card-${sh._id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#E2B05E] to-[#F0C47A] flex items-center justify-center text-[#0B0E14] font-bold text-lg"><Crown weight="fill" className="w-6 h-6" /></div>
                  <div><h3 className="font-semibold text-[#F3F4F6]">{sh.name}</h3><p className="text-sm text-[#9CA3AF]">{sh.email}</p></div>
                </div>
                <Badge className={sh.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'}>
                  {sh.is_active !== false ? t('activeLabel') : t('inactiveLabel')}
                </Badge>
              </div>
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-[#9CA3AF]" />
                <span className="text-[#9CA3AF]">{sh.employee_ids?.length || 0} {t('assignedEmployees')}</span>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-[#242E42]">
                <Button size="sm" variant="ghost" onClick={() => handleEdit(sh)} className="text-[#9CA3AF] hover:text-[#3AB8D6]"><PencilSimple className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => handleDelete(sh._id)} className="text-[#9CA3AF] hover:text-red-400"><Trash className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>

        {stakeholders.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <Crown className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noStakeholders')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default StakeholdersPage;
